import { useState } from "react";
import { useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { MobilePage } from "../MobilePage";
import { OfferDot } from "../ui";
import { offerColor, useAllOffers } from "../useOffers";
import { useSalesTexts, type SalesText } from "./ai";

const KIND: Record<string, string> = {
  open: "Frase de apertura",
  open_sin_sitio: "Frase de apertura (sin sitio web)",
  pitch: "Propuesta en 30 segundos",
  sol: "Cómo lo resuelves",
  price: "Precio sugerido",
  next: "Siguiente paso",
  quant: "Tu número (lo que le cuesta el problema)",
  risk: "Riesgo cero (promesa)",
  obj: "Respuesta a objeción",
};
const CHANNEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  email: "Correo",
  llamada: "Guion de llamada",
};

type Tpl = {
  id: number;
  offer_id: number | null;
  channel: string;
  name: string;
  subject: string | null;
  body: string;
  active: boolean;
  pending_validation: boolean;
};
type Item = {
  key: string;
  offer: string | null; // slug
  what: string;
  body: string;
  subject?: string | null;
  status: "pendiente" | "aprobado" | "descartado";
  src: { t: "text"; row: SalesText } | { t: "tpl"; row: Tpl };
};
const FILTERS = [
  ["pendiente", "Pendientes"],
  ["aprobado", "Aprobados"],
  ["descartado", "Descartados"],
] as const;

/**
 * Textos por validar (Bloque G, decisión 9 de Ian): promesas y plantillas marcadas
 * "pendiente de validar". La IA solo usa en guiones y propuestas los que apruebas.
 */
export const TextsToValidate = () => {
  const { data: texts } = useSalesTexts();
  const { data: offers } = useAllOffers();
  const { data: tpls } = useQuery({
    queryKey: ["templates", "validar"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("templates")
        .select("*")
        .order("offer_id")
        .order("id");
      if (error) throw error;
      return data as Tpl[];
    },
  });
  const [filter, setFilter] = useState<Item["status"]>("pendiente");
  const [offer, setOffer] = useState<string>("");
  const slugOf = (id: number | null) =>
    offers?.find((o) => o.id === id)?.slug ?? null;
  // Plantillas: pendiente = pending_validation; aprobada = activa sin pendiente; descartada = inactiva
  const items: Item[] = [
    ...(texts ?? []).map((row) => ({
      key: `t${row.id}`,
      offer: row.offer_slug,
      what:
        row.kind === "obj"
          ? `${KIND.obj}: «${row.label}»`
          : (KIND[row.kind] ?? row.kind),
      body: row.body,
      status: row.status,
      src: { t: "text" as const, row },
    })),
    ...(tpls ?? []).map((row) => ({
      key: `p${row.id}`,
      offer: slugOf(row.offer_id),
      what: `Plantilla de ${CHANNEL[row.channel] ?? row.channel}: ${row.name}`,
      body: row.body,
      subject: row.subject,
      status: (!row.active
        ? "descartado"
        : row.pending_validation
          ? "pendiente"
          : "aprobado") as Item["status"],
      src: { t: "tpl" as const, row },
    })),
  ];
  const n = (s: Item["status"]) => items.filter((i) => i.status === s).length;
  const shown = items.filter(
    (i) => i.status === filter && (!offer || i.offer === offer),
  );

  return (
    <MobilePage title="Textos por validar">
      <div className="flex max-w-4xl flex-col gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight max-md:hidden">
            Textos por validar
          </h1>
          <p className="text-sm text-muted-foreground">
            Promesas de los guiones y propuestas (incluidos los de riesgo cero)
            y las plantillas. La IA solo usa en guiones y propuestas los que
            apruebes; mientras tanto, se muestran con la marca “pendiente”.
            Variables:{" "}
            {"{{empresa}} {{municipio}} {{actividad}} {{rango}} {{sitio}}"}{" "}
            (plantillas: también{" "}
            {"{{nombre}} {{giro}} {{remitente}} {{agenda}}"}).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-lg border">
            {FILTERS.map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={cn(
                  "px-3 py-1.5 text-sm font-semibold",
                  filter === k
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground",
                )}
              >
                {l} ({n(k)})
              </button>
            ))}
          </div>
          <select
            className="h-9 rounded-lg border bg-card px-2 text-sm"
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
            aria-label="Producto"
          >
            <option value="">Todos los productos</option>
            {offers?.map((o) => (
              <option key={o.id} value={o.slug}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        {shown.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No hay textos en esta lista.
          </p>
        )}
        {shown.map((i) => (
          <TextRow
            key={i.key}
            i={i}
            offerName={offers?.find((o) => o.slug === i.offer)?.name}
          />
        ))}
      </div>
    </MobilePage>
  );
};

const TextRow = ({ i, offerName }: { i: Item; offerName?: string }) => {
  const [edit, setEdit] = useState<{ body: string; subject: string } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const qc = useQueryClient();
  const sb = getSupabaseClient();
  const save = async (
    status: Item["status"],
    body?: string,
    subject?: string,
  ) => {
    setBusy(true);
    const now = new Date().toISOString();
    const { data, error } =
      i.src.t === "text"
        ? await sb
            .from("sales_texts")
            .update({
              status,
              reviewed_at: now,
              updated_at: now,
              ...(body != null ? { body } : {}),
            })
            .eq("id", i.src.row.id)
            .select("id")
        : await sb
            .from("templates")
            .update({
              pending_validation: status === "pendiente",
              active: status !== "descartado",
              ...(body != null ? { body } : {}),
              ...(subject != null ? { subject: subject || null } : {}),
            })
            .eq("id", i.src.row.id)
            .select("id");
    setBusy(false);
    if (error || !data?.length)
      return notify(
        error?.message ?? "Solo un administrador puede validar textos",
        { type: "error" },
      );
    notify(
      status === "aprobado"
        ? "Aprobado"
        : status === "descartado"
          ? "Descartado"
          : "Guardado",
      { type: "success" },
    );
    setEdit(null);
    qc.invalidateQueries({ queryKey: ["sales_texts"] });
    qc.invalidateQueries({ queryKey: ["templates"] });
  };
  return (
    <Card className="gap-2 p-3.5">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {i.offer && <OfferDot color={offerColor({ slug: i.offer })} />}
        <b>{offerName ?? "Sin producto"}</b>
        <span className="text-muted-foreground">· {i.what}</span>
      </div>
      {edit ? (
        <div className="flex flex-col gap-2">
          {i.src.t === "tpl" && i.src.row.channel === "email" && (
            <input
              className="h-9 rounded-lg border bg-card px-2 text-sm"
              value={edit.subject}
              onChange={(e) => setEdit({ ...edit, subject: e.target.value })}
              aria-label="Asunto"
              placeholder="Asunto"
            />
          )}
          <Textarea
            rows={4}
            value={edit.body}
            onChange={(e) => setEdit({ ...edit, body: e.target.value })}
            aria-label="Texto"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy || !edit.body.trim()}
              onClick={() =>
                save("aprobado", edit.body.trim(), edit.subject.trim())
              }
            >
              <Check /> Guardar y aprobar
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setEdit(null)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <>
          {i.subject && (
            <p className="text-sm">
              <b>Asunto:</b> {i.subject}
            </p>
          )}
          <p className="rounded-lg border-l-4 border-primary bg-secondary/60 px-3 py-2 text-sm whitespace-pre-line">
            {i.body}
          </p>
          <div className="flex flex-wrap gap-2">
            {i.status !== "aprobado" && (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => save("aprobado")}
              >
                <Check /> Aprobar
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                setEdit({ body: i.body, subject: i.subject ?? "" })
              }
            >
              <Pencil /> Editar
            </Button>
            {i.status !== "descartado" ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-[var(--critical)]"
                disabled={busy}
                onClick={() => save("descartado")}
              >
                <X /> Descartar
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => save("pendiente")}
              >
                Regresar a pendientes
              </Button>
            )}
          </div>
        </>
      )}
    </Card>
  );
};
